import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { Appointment } from '@geely/shared';
import { formatDate, formatTime, getStatusLabel } from '@/lib/utils';

const COLUMNS = [
  'ID', 'Data', 'Horário', 'Cliente', 'CPF', 'Telefone', 'Email',
  'Modelo', 'Versão', 'Placa', 'VIN', 'Quilometragem',
  'Consentimento LGPD', 'Confirmação', 'Status', 'Criado em',
];

function toRows(appointments: Appointment[]): string[][] {
  return appointments.map((apt) => [
    apt.id,
    formatDate(apt.date),
    formatTime(apt.time),
    apt.customerName,
    apt.customerCpf,
    apt.customerPhone,
    apt.customerEmail,
    apt.model?.name ?? '—',
    apt.version?.name ?? '—',
    apt.vehiclePlate,
    apt.vehicleVin ?? '—',
    String(apt.vehicleMileage),
    apt.lgpdConsent ? 'Sim' : 'Não',
    apt.confirmedAt ? 'Confirmado' : '—',
    getStatusLabel(apt.status),
    formatDate(apt.createdAt.split('T')[0]),
  ]);
}

function filename(ext: string) {
  return `agendamentos_${new Date().toISOString().split('T')[0]}.${ext}`;
}

export function exportCSV(appointments: Appointment[]) {
  const ws = XLSX.utils.aoa_to_sheet([COLUMNS, ...toRows(appointments)]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Agendamentos');
  XLSX.writeFile(wb, filename('csv'), { bookType: 'csv' });
}

export function exportExcel(appointments: Appointment[]) {
  const ws = XLSX.utils.aoa_to_sheet([COLUMNS, ...toRows(appointments)]);
  const widths: Record<number, number> = { 0: 36, 3: 24, 4: 16, 5: 16, 6: 28, 7: 18, 8: 18, 9: 12, 10: 20 };
  ws['!cols'] = COLUMNS.map((_, i) => ({ wch: widths[i] ?? 14 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Agendamentos');
  XLSX.writeFile(wb, filename('xlsx'));
}

const PDF_COLUMNS = [
  'Data', 'Horário', 'Cliente', 'CPF', 'Telefone', 'Email',
  'Modelo/Versão', 'Placa', 'Chassi', 'Km', 'LGPD', 'Confirmação', 'Status',
];

function toPdfRows(appointments: Appointment[]): string[][] {
  return appointments.map((apt) => [
    formatDate(apt.date),
    formatTime(apt.time),
    apt.customerName,
    apt.customerCpf,
    apt.customerPhone,
    apt.customerEmail,
    apt.model?.name
      ? `${apt.model.name}${apt.version?.name ? ` / ${apt.version.name}` : ''}`
      : '—',
    apt.vehiclePlate,
    apt.vehicleVin ?? '—',
    String(apt.vehicleMileage),
    apt.lgpdConsent ? 'Sim' : 'Não',
    apt.confirmedAt ? 'Confirmado' : '—',
    getStatusLabel(apt.status),
  ]);
}

export function exportPDF(
  appointments: Appointment[],
  options?: { dateFrom?: string; dateTo?: string },
) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  const periodParts: string[] = [];
  if (options?.dateFrom) periodParts.push(formatDate(options.dateFrom));
  if (options?.dateTo) periodParts.push(formatDate(options.dateTo));
  const period = periodParts.length === 2
    ? ` · ${periodParts[0]} a ${periodParts[1]}`
    : periodParts.length === 1
    ? ` · a partir de ${periodParts[0]}`
    : '';

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(`Agendamentos${period}`, 14, 15);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(130);
  doc.text(`Gerado em ${new Date().toLocaleDateString('pt-BR')} · ${appointments.length} registro(s)`, 14, 21);

  autoTable(doc, {
    head: [PDF_COLUMNS],
    body: toPdfRows(appointments),
    startY: 26,
    styles: { fontSize: 6.5, cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 }, overflow: 'linebreak' },
    headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold', fontSize: 6.5 },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    columnStyles: {
      0: { cellWidth: 18 },  // Data
      1: { cellWidth: 16 },  // Horário
      2: { cellWidth: 24 },  // Cliente
      3: { cellWidth: 21 },  // CPF
      4: { cellWidth: 22 },  // Telefone
      5: { cellWidth: 30 },  // Email
      6: { cellWidth: 25 },  // Modelo/Versão
      7: { cellWidth: 17 },  // Placa
      8: { cellWidth: 22 },  // Chassi
      9: { cellWidth: 15 },  // Km
      10: { cellWidth: 13 }, // LGPD
      11: { cellWidth: 22 }, // Confirmação
      12: { cellWidth: 19 }, // Status
    },
    margin: { left: 14, right: 14 },
  });

  doc.save(filename('pdf'));
}
